import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import ReactECharts, { EChartsOption } from "echarts-for-react";
import { dbrVersionData } from "service/service";
import LoaderComponent from "components/Loader";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import moment from "moment";
import DateTimeIcon from "assets/fluent-mdl2_date-time.svg";


interface DBRItem {
  compute_type: string;
  _id: string;
  total_count: number;
  versions: string[];
  major_version: string;
  eod_support_date: string;
}

interface DBRComponentProps {
  startDate: string;
  endDate: string;
  env: string;
  sector: string;
  location: string;
  selectedDateInString: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ background: '#003771' }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function findHighestVersion(arr: DBRItem[]) {
  if (!Array.isArray(arr) || arr.length === 0) return null;

  return arr.reduce((highest, current) => {
    // Extract number from name (handles cases like "custom:custom-prod__14.x")
    const getVersion = (str: string) => {
      if (!str) return -Infinity;
      const dotIdx = str.lastIndexOf('.x');
      if (dotIdx === -1) return -Infinity;

      let digits = '';
      for (let i = dotIdx - 1; i >= 0; i--) {
        const char = str[i];
        if (char >= '0' && char <= '9') {
          digits = char + digits;
        } else {
          break;
        }
      }
      const version = parseInt(digits, 10);
      return isNaN(version) ? -Infinity : version;
    };

    const highestVer = getVersion(highest._id);
    const currentVer = getVersion(current._id);

    return currentVer > highestVer ? current : highest;
  });
}


const DBRComponent: React.FC<DBRComponentProps> = ({
  startDate,
  endDate,
  env,
  sector,
  location,
  selectedDateInString,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [dbrVersion, setDbrVersion] = useState<DBRItem[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [jobVersions, setJobVersions] = useState<DBRItem[]>([])
  const [interactiveVersions, setInteractiveVersions] = useState<DBRItem[]>([])
  const [highestJobVersion, setHighestJobVersion] = useState<DBRItem | null>(null);
  const [highestInteractiveVersion, setHighestInteractiveVersion] = useState<DBRItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    if (jobVersions.length > 0) {
      setHighestJobVersion(findHighestVersion(jobVersions));
    }
  }, [jobVersions]);

  useEffect(() => {
    if (interactiveVersions.length > 0) {
      setHighestInteractiveVersion(findHighestVersion(interactiveVersions));
    }
  }, [interactiveVersions]);
  /** Fetch DBR version data */
  const fetchDBRVersions = useCallback(async () => {
    try {
      setLoading(true);
      const startFormatted = startDate.split(" ")[0];
      const endFormatted = endDate.split(" ")[0];
      const resp = await dbrVersionData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
      if (resp?.success) {
        setDbrVersion(resp.data || []);
        const interactiveArray = resp.data.filter((item: DBRItem) => item.compute_type === "Interactive");
        const jobArray = resp.data.filter((item: DBRItem) => item.compute_type === "Job");

        setInteractiveVersions(interactiveArray)
        setJobVersions(jobArray)
      } else {
        setDbrVersion([]);
      }
    } catch (err) {
      console.error("Failed to fetch DBR versions:", err);
      setDbrVersion([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, env, sector, location, selectedDateInString]);

  /** Refetch when filters change */
  useEffect(() => {
    fetchDBRVersions();
  }, [fetchDBRVersions]);

  const filteredInteractiveData = useMemo(
    () =>
      interactiveVersions.map((item) => ({
        name: `${item._id}   •  ${item.eod_support_date
          ? moment(item.eod_support_date).format("MM-DD-YYYY")
          : "end of suport"
          }`,
        value: item.total_count
      })),
    [interactiveVersions]
  );

  const filteredJobData = useMemo(
    () =>
      jobVersions.map((item) => ({
        name: `${item._id}   •  ${item.eod_support_date
          ? moment(item.eod_support_date).format("MM-DD-YYYY")
          : "end of suport"
          }`,
        value: item.total_count
      })),
    [jobVersions]
  );


  const handlePieClickEvents = (params: any) => {
    navigate(
      `/databricksADB/dbr-version-list?version=${encodeURIComponent(params.name)}&compute_type=${encodeURIComponent(selectedTab === 0 ? 'interactive' : 'job')}`,
      {
        state: { startDate, endDate, env, sector, location, selectedDateInString },
      }
    );
  };

  const handleLegendClickEvents = (params: any, cluster_source: string) => {
    const deselectedKeys = Object.keys(params.selected).filter(
      (key) => params.selected[key] === false
    );

    console.log("Deselected legends:", deselectedKeys);

    // Determine source list based on cluster_source
    const sourceArray =
      cluster_source === "interactive" ? interactiveVersions : jobVersions;

    // If all legends are selected (nothing false)
    if (deselectedKeys.length === 0) {
      const highest = findHighestVersion(sourceArray);
      if (cluster_source === "interactive") {
        setHighestInteractiveVersion(highest);
      } else {
        setHighestJobVersion(highest);
      }
      return;
    }


    const filtered = sourceArray.filter(
      (obj: DBRItem) => !deselectedKeys.includes(obj._id)
    );

    const newHighest = findHighestVersion(filtered);

    if (cluster_source === "interactive") {
      setHighestInteractiveVersion(newHighest);
    } else {
      setHighestJobVersion(newHighest);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const getEodSupportDate = (
    id: string,
    interactiveVersions: Array<{ _id: string; eod_support_date?: string | null }>
  ): string => {
    const match = interactiveVersions.find(item => item._id === id);

    return match?.eod_support_date
      ? moment(match.eod_support_date).format("MM-DD-YYYY")
      : "end of suport";
  };

  const interactiveChart: EChartsOption = {
    backgroundColor: '#ffffff',
    tooltip: {
      backgroundColor: '#ffffff',
      trigger: 'item',
      textStyle: { color: '#000000' },
    },
    legend: {
      orient: 'vertical',
      top: 50,
      left: 3,
      textStyle: {
        color: '#000000'
      }
    },
    series: [
      {
        name: "Versions",
        type: "pie",
        radius: "85%",
        center: ["65%", "50%"],
        data: filteredInteractiveData,
        avoidLabelOverlap: false,
        label: {
          show: true,
          position: "inside",
          color: '#ffffff',
          fontSize: 15,
          formatter: "{c} ({d}%)",
        },
      },
    ],
  };

  const jobChart: EChartsOption = {
    backgroundColor: '#ffffff',
    tooltip: {
      backgroundColor: '#ffffff',
      trigger: 'item',
      textStyle: { color: '#000000' },
    },
    legend: {
      orient: 'vertical',
      top: 50,
      left: 3,
      textStyle: {
        color: '#000000'
      }
    },
    series: [
      {
        name: "Versions",
        type: "pie",
        radius: "85%",
        center: ["65%", "50%"],
        data: filteredJobData,
        avoidLabelOverlap: false,
        label: {
          show: true,
          position: "inside",
          color: '#ffffff',
          fontSize: 15,
          formatter: "{c} ({d}%)",
        },
      },
    ],
  };

  const renderContent = () => {
    if (loading) {
      return <LoaderComponent size={50} />;
    }

    if (dbrVersion.length === 0) {
      return (
        <Box sx={{ color: "#000000", textAlign: "center", paddingTop: "30%" }}>
          No data available
        </Box>
      );
    }

    return (
      <Grid container spacing={2} sx={{ height: '100%' }}>
        {/* Left Column: Latest Version Info */}
        <Grid item xs={4} sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          position: 'relative',
          //borderRight: '1px solid rgba(255, 255, 255, 0.5)',
          pt: 2
        }}>
          <Typography variant='widgetTitle' sx={{ mb: 1, color : '#000000' }}>
            DBR Versions
          </Typography>

          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: 1 }}>
              {highestInteractiveVersion && highestJobVersion && (
                <Typography
                  variant='h6'
                  onClick={() =>
                    navigate(
                      `/databricksADB/dbr-version-list?version=${encodeURIComponent(
                        selectedTab === 0 ? highestInteractiveVersion?._id : highestJobVersion?._id
                      )}`,
                      {
                        state: { startDate, endDate, env, sector, location, selectedDateInString },
                      }
                    )
                  }
                  sx={{
                    color : '#000000',
                    fontWeight: '400',
                    cursor: 'pointer',
                    fontSize: '48px',
                    '&:hover': { color: '#21ddebff', transform: 'scale(1.1)' },
                  }}
                >
                  <span style={{ fontSize: '14px' }}>V</span>
                  {selectedTab === 0 ? highestInteractiveVersion?._id : highestJobVersion?._id}
                </Typography>
              )}
              <div
                onClick={handleClickListItem}
                style={{ display: 'inline-flex', cursor: 'pointer' }}
              >
                <InfoOutlineIcon
                  sx={{
                    color: '#39ACE7',
                    fontSize: '20px',
                    transition: '0.3s ease',
                    '&:hover': { color: '#21ddebff', transform: 'scale(1.1)' },
                  }}
                />
              </div>
            </Box>

            <Typography variant='body2' sx={{ fontWeight: 100, color : '#71758F' }}>
              Support End Date : {getEodSupportDate(selectedTab === 0 ? highestInteractiveVersion?._id! : highestJobVersion?._id!, selectedTab === 0 ? interactiveVersions : jobVersions)}
            </Typography>
            <Typography variant='h6' sx={{ fontWeight: 'bold', color: '#000000' }}>
              Latest Version
            </Typography>
          </Box>
        </Grid>

        {/* Right Column: Charts */}
        <Grid item xs={8}>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ flexGrow: 1 }} />
              <Tabs
                value={selectedTab}
                onChange={handleChange}
                aria-label="dbr tabs"
                sx={{
                  '& .MuiTabs-flexContainer': {
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  },
                  '& .MuiTab-root': {
                    ...theme.typography.tabLabel,
                    textTransform: 'none',
                    backgroundColor: 'transparent !important',
                    color: '#A9ADCB',
                    borderRadius: '10px 10px 0 0',
                  },
                  '& .MuiTab-root.Mui-selected': {
                    borderBottom: `9px solid ${selectedTab === 0 ? '#009BD8' : '#009BD8'}`,
                    color: '#009BD8 !important',
                    backgroundColor: 'transparent !important',
                  },
                }}
              >
                <Tab label="Interactive" {...a11yProps(0)} />
                <Tab label="Job" {...a11yProps(1)} sx={{ backgroundColor: '#fff !important' }} />
              </Tabs>
              <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', pb: 1, cursor: 'pointer' }} onClick={() => navigate('/databricksADB/dbr-tab-table', {
                state: { startDate, endDate, env, sector, location, selectedDateInString },
              })}>
                <img src={DateTimeIcon} alt="date-time" style={{ width: '24px', height: '24px' }} />
              </Box>
            </Box>
            <CustomTabPanel value={selectedTab} index={0}>
              <ReactECharts
                option={interactiveChart}
                onEvents={{
                  click: handlePieClickEvents,
                  legendselectchanged: (params: any) => handleLegendClickEvents(params, 'interactive')
                }}
                style={{ height: 340 }}
              />
            </CustomTabPanel>
            <CustomTabPanel value={selectedTab} index={1}>
              <ReactECharts
                option={jobChart}
                onEvents={{
                  click: handlePieClickEvents,
                  legendselectchanged: (params: any) => handleLegendClickEvents(params, 'job')
                }}
                style={{ height: 340 }}
              />
            </CustomTabPanel>
          </Box>
        </Grid>
        <Menu
          id="lock-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          {selectedTab === 0 && highestInteractiveVersion?.versions.map((option, index) => (
            <MenuItem
              key={option}
            >
              {option}
            </MenuItem>
          ))}
          {selectedTab === 1 && highestJobVersion?.versions.map((option, index) => (
            <MenuItem
              key={option}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      </Grid>
    );
  };

  return (
    <Box sx={{ minHeight: 400 }}>
      {renderContent()}
    </Box>
  );
};

export default DBRComponent;